import { Form, Popup } from 'devextreme-react';
import { RequiredRule } from 'devextreme-react/data-grid';
import { ButtonItem, GroupItem, Label, SimpleItem } from 'devextreme-react/form';
import React, { useEffect, useRef, useState } from 'react';
import { getListApi } from '../../apiUtils';
import '../../assets/bulk-modal.scss';
import { useAssetClass } from '../../contexts/assetClass';
import { useLoading } from '../../contexts/loadingContext';
import { useTranslation } from '../../contexts/translation';
import { CapacityCategory } from '../../types/capacityCategory.types';
import { DataGridActionType } from '../../types/dataGridActionTypes';
import { StepGroup } from '../../types/stepGroup.types';
import { API_URL } from '../../utils/apiUrl';
import './EditCreateModal.scss';
import './TasksBulkEditModal.scss';

type Props = {
  data: any;
  stepGroupList?: StepGroup[];
  isVisible: boolean;
  mutate?: () => void;
  onClose: () => void;
  onSubmit: (data: any, actionType: DataGridActionType) => void;
  actionType: DataGridActionType;
  transFormName: string;
  assetCycleId: number;
};

/**
 * Modalová komponent pro vytváření/editaci záznamů typu Asset cycle step v datagridu
 */
export default function AssetCycleStepGroupModal({
  isVisible,
  data,
  stepGroupList,
  actionType,
  onClose,
  onSubmit,
  transFormName,
  assetCycleId,
}: Props) {
  const { assetClass } = useAssetClass();
  const { translate } = useTranslation();
  const formRef = useRef<Form>(null);
  const { loading } = useLoading();
  const [selectedUnit, setSelectedUnit] = useState<string>();
  const [formData, setFormData] = useState<any>({
    ...data,
    ...(actionType === DataGridActionType.Insert && { visible: true }),
  });
  const [isCounterVisible, setCounterVisible] = useState<boolean>(true);
  const [capacityCategoryId, setCapacityCategoryId] = useState<number | null>();
  const [capacityCategories, setCapacityCategories] = useState<CapacityCategory[] | null>();

  useEffect(() => {
    setFormData({
      ...data,
      ...(actionType === DataGridActionType.Insert && { visible: true }),
      frequency: data && data.frequency ? data.frequency : 1, // Defaultní hodnota bude 1. Pozor, pokud by byla skutečně frequence 0, v editu se přepíše na 1.
    });
    getCapacityCategories();
  }, []);

  useEffect(() => {
    handleCounterVisibility();
  }, [formData]);

  const handleCounterVisibility = (): boolean => {
    // Skryjeme counter, pokud je měsíční frekvence
    const isFrequencyOne = formData.frequency === 1;
    setCounterVisible(!isFrequencyOne);
    return isFrequencyOne;
  };

  const onFrequencyChanged = () => {
    const isFrequencyOne = handleCounterVisibility();
    if (isFrequencyOne)
      setFormData({
        ...formData,
        counter: 0,
      });
  };

  const getCapacityCategories = async () => {
    getListApi({
      url: `${API_URL.CAPACITY_CATEGORY_LIST}?parentId=${assetClass}`,
      params: {},
      callAfterSuccess: (result) => {
        setCapacityCategories(result);
        getAssetCycle();
      },
    });
  };

  const getAssetCycle = async () => {
    getListApi({
      url: `${API_URL.ASSET_CYCLE_ITEM}?id=${assetCycleId}`,
      params: {},
      callAfterSuccess: (result) => {
        setCapacityCategoryId(result.capacityCategoryId);
      },
    });
  };

  return (
    <Popup
      visible={isVisible}
      dragEnabled={false}
      closeOnOutsideClick
      onHiding={onClose}
      showTitle
      title={translate!('Asset cycle step group', transFormName)}
      width={'600px'}
      height={'auto'}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData, actionType);
        }}
      >
        <Form
          formData={formData}
          showColonAfterLabel={true}
          showValidationSummary={true}
          ref={formRef}
        >
          {actionType !== DataGridActionType.Update ? (
            <GroupItem>
              <SimpleItem
                dataField="capacityCategoryId"
                editorType="dxSelectBox"
                editorOptions={{
                  items: capacityCategories,
                  valueExpr: 'id',
                  displayExpr: 'name',
                  showClearButton: true,
                  value: capacityCategoryId,
                  onValueChanged: (e: any) => {
                    setFormData({ ...formData, stepGroupId: undefined });
                    setCapacityCategoryId(e.value);
                  },
                }}
              >
                <Label text={translate!('Capacity category', transFormName)} />
              </SimpleItem>
              <SimpleItem
                dataField="stepGroupId"
                editorType="dxSelectBox"
                editorOptions={{
                  items: capacityCategoryId
                    ? stepGroupList?.filter((x) => x.capacityCategoryId === capacityCategoryId)
                    : stepGroupList,
                  valueExpr: 'id',
                  displayExpr: 'name',
                }}
              >
                <Label text={translate!('Step group', transFormName)} />
                <RequiredRule message={translate!('Select step group', transFormName)} />
              </SimpleItem>
            </GroupItem>
          ) : (
            <GroupItem>
              <SimpleItem
                dataField="stepGroupName"
                editorType="dxTextBox"
                editorOptions={{
                  readOnly: true,
                }}
              >
                <Label text={translate!('Step group', transFormName)} />
              </SimpleItem>
            </GroupItem>
          )}
          <GroupItem>
            <SimpleItem
              dataField="frequency"
              editorType="dxNumberBox"
              editorOptions={{ onValueChanged: () => onFrequencyChanged() }}
            >
              <Label text={translate!('Frequency', transFormName)} />
            </SimpleItem>
          </GroupItem>
          {isCounterVisible && (
            <GroupItem>
              <SimpleItem dataField="counter" editorType="dxNumberBox">
                <Label text={translate!('Counter', transFormName)} />
              </SimpleItem>
            </GroupItem>
          )}
          <GroupItem cssClass="buttons-group" colCountByScreen={{ xs: 2, sm: 2, md: 2, lg: 2 }}>
            <ButtonItem
              buttonOptions={{
                type: 'success',
                text: translate!('Save', ''),
                width: 120,
                useSubmitBehavior: true,
              }}
            />

            <ButtonItem
              buttonOptions={{
                type: 'normal',
                text: translate!('Close', ''),
                onclick: () => onClose(),
                width: 120,
              }}
            />
          </GroupItem>
        </Form>
      </form>
    </Popup>
  );
}
